/**
 * Forms Templates Component
 * 
 * Allows experts to upload, manage, and configure PDF document templates.
 * Used in Profile page for template management.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { useToast } from '../../Context/ToastContext';
import { useConfirm } from '../../Context/ConfirmContext';
import { useFeature } from '../../Context/CapabilityContext';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(99, 102, 241, 0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const Section = styled.div`
  background: rgba(99, 102, 241, 0.05);
  border: none;
  border-radius: 12px;
  border-left: 3px solid rgba(99, 102, 241, 0.5);
  padding: 24px;
  margin-bottom: 20px;
  backdrop-filter: blur(8px);
  animation: ${fadeIn} 0.3s ease;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ReprocessButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(99, 102, 241, 0.2);
  border: 1px solid rgba(99, 102, 241, 0.4);
  border-radius: 6px;
  color: #a5b4fc;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.3);
    border-color: rgba(99, 102, 241, 0.6);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 14px;
    height: 14px;
    ${props => props.$spinning && css`animation: ${spin} 1s linear infinite;`}
  }
`;

const Description = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 20px 0;
  line-height: 1.5;
`;

const UploadArea = styled.div`
  border: 2px dashed ${props => props.$isDragOver ? 'rgba(99, 102, 241, 0.8)' : 'rgba(99, 102, 241, 0.3)'};
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  background: ${props => props.$isDragOver ? 'rgba(99, 102, 241, 0.1)' : 'rgba(17, 24, 39, 0.5)'};
  transition: all 0.2s ease;
  cursor: pointer;
  margin-bottom: 20px;
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.6);
    background: rgba(99, 102, 241, 0.08);
  }
`;

const UploadIcon = styled.div`
  font-size: 40px;
  margin-bottom: 12px;
`;

const UploadText = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 6px;
`;

const UploadHint = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const HiddenInput = styled.input`
  display: none !important;
  visibility: hidden !important;
  position: absolute !important;
  width: 0 !important;
  height: 0 !important;
  opacity: 0 !important;
  pointer-events: none !important;
`;

const TemplateGrid = styled.div`
  display: grid;
  gap: 12px;
`;

const TemplateCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: rgba(31, 41, 55, 0.6);
  border: 1px solid rgba(75, 85, 99, 0.3);
  border-radius: 10px;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(45, 55, 72, 0.7);
    border-color: rgba(99, 102, 241, 0.4);
  }
  
  ${props => props.$selected && css`
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.5);
  `}
`;

const TemplateIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const TemplateInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TemplateName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #e8eaed;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TemplateDetails = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 3px;
  
  span:not(:first-child) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 120px;
  }
`;

const CategoryBadge = styled.span`
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  background: rgba(99, 102, 241, 0.2);
  color: #a5b4fc;
`;

const FieldCount = styled.span`
  color: ${props => props.$mapped ? '#34d399' : '#fbbf24'};
`;

const QuestionnaireStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  
  ${props => props.$status === 'ready' && css`
    background: rgba(16, 185, 129, 0.15);
    color: #34d399;
  `}
  
  ${props => props.$status === 'generating' && css`
    background: rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
  `}
  
  ${props => props.$status === 'failed' && css`
    background: rgba(239, 68, 68, 0.15);
    color: #f87171;
  `}
  
  ${props => props.$status === 'pending' && css`
    background: rgba(251, 191, 36, 0.15);
    color: #fbbf24;
  `}
  
  ${props => props.$status === 'none' && css`
    background: rgba(107, 114, 128, 0.15);
    color: #9ca3af;
  `}
`;

const TemplateActions = styled.div`
  display: flex;
  gap: 6px;
`;

const IconButton = styled.button`
  padding: 6px 8px;
  border-radius: 6px;
  border: none;
  background: rgba(55, 65, 81, 0.5);
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(99, 102, 241, 0.3);
    color: #fff;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const DeleteButton = styled(IconButton)`
  &:hover {
    background: rgba(239, 68, 68, 0.3);
    color: #f87171;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 24px;
  color: rgba(255, 255, 255, 0.5);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    opacity: 0.4;
  }
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid rgba(99, 102, 241, 0.2);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 20px auto;
`;

const StatusMessage = styled.div`
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  ${props => props.$type === 'success' && css`
    background: rgba(16, 185, 129, 0.15);
    border: 1px solid rgba(16, 185, 129, 0.3);
    color: #34d399;
  `}
  
  ${props => props.$type === 'error' && css`
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #f87171;
  `}
  
  ${props => props.$type === 'info' && css`
    background: rgba(99, 102, 241, 0.15);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: #a5b4fc;
  `}
`;

// ============================================
// UPLOAD MODAL
// ============================================

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000000;
  padding: 20px;
`;

const Modal = styled.div`
  background: #1a1f2e;
  border: 1px solid rgba(75, 85, 99, 0.4);
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  animation: ${fadeIn} 0.2s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 24px 24px 16px 24px;
  flex-shrink: 0;
`;

const ModalBody = styled.div`
  padding: 0 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: rgba(99, 102, 241, 0.5) transparent;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.5);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.4);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.6);
  }
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 20px 0;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(75, 85, 99, 0.5);
  background: rgba(17, 24, 39, 0.8);
  color: #e8eaed;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.6);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(75, 85, 99, 0.5);
  background: rgba(17, 24, 39, 0.8);
  color: #e8eaed;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.6);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 24px 24px;
  border-top: 1px solid rgba(75, 85, 99, 0.3);
  flex-shrink: 0;
  background: #1a1f2e;
`;

const Button = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$primary ? css`
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: white;
    
    &:hover:not(:disabled) {
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  ` : css`
    background: rgba(55, 65, 81, 0.5);
    border: 1px solid rgba(75, 85, 99, 0.5);
    color: #d1d5db;
    
    &:hover {
      background: rgba(75, 85, 99, 0.5);
    }
  `}
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(31, 41, 55, 0.6);
  border-radius: 8px;
  margin-bottom: 16px;
`;

const FileName = styled.div`
  flex: 1;
  font-size: 13px;
  color: #e8eaed;
`;

const FileSize = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
`;

// ============================================
// ICONS
// ============================================

const Icons = {
  document: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14,2 14,8 20,8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <line x1="10" y1="9" x2="8" y2="9"/>
    </svg>
  ),
  edit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  trash: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3,6 5,6 21,6"/>
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
    </svg>
  ),
  upload: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
      <polyline points="17,8 12,3 7,8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  refresh: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M23 4v6h-6"/>
      <path d="M1 20v-6h6"/>
      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
    </svg>
  ),
  eye: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  questionMark: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  )
};

// ============================================
// CATEGORIES
// ============================================

const CATEGORIES = [
  'Work Permit',
  'Study Permit',
  'Visitor Visa',
  'Express Entry',
  'Family Sponsorship',
  'Provincial Nominee',
  'Refugee Claim',
  'Citizenship',
  'Travel Document',
  'Other'
];

// ============================================
// COMPONENT
// ============================================

const DocumentTemplates = ({ onViewDetails }) => {
  const axios = useAuthenticatedAxios();
  const fileInputRef = useRef(null);
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const showIrcc = useFeature('irccForms');

  // State
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    formCode: '',
    category: 'Work Permit',
    description: '',
    version: '1.0'
  });
  
  // IRCC Import state
  const [showIRCCModal, setShowIRCCModal] = useState(false);
  const [irccForms, setIrccForms] = useState([]);
  const [loadingIRCC, setLoadingIRCC] = useState(false);
  const [importingForm, setImportingForm] = useState(null);
  const [irccError, setIrccError] = useState(null);
  const [urlHealth, setUrlHealth] = useState({});
  
  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      const response = await axios.get('/document-templates');
      if (response.data.success) {
        setTemplates(response.data.templates);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setMessage({ type: 'error', text: 'Failed to load document templates' });
    } finally {
      setLoading(false);
    }
  }, [axios]);
  
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);
  
  // Load available IRCC forms and URL health status
  const loadIRCCForms = useCallback(async () => {
    setLoadingIRCC(true);
    try {
      console.log('[IRCC] Loading available forms...');
      
      // Load forms and URL health in parallel
      const [formsResponse, healthResponse] = await Promise.all([
        axios.get('/document-templates/ircc-forms'),
        axios.get('/document-templates/ircc-url-health').catch(() => ({ data: { health: [] } }))
      ]);
      
      console.log('[IRCC] Forms Response:', formsResponse.data);
      
      if (formsResponse.data.success) {
        setIrccForms(formsResponse.data.forms || []);
        console.log('[IRCC] Loaded', formsResponse.data.forms?.length || 0, 'forms');
      } else {
        console.warn('[IRCC] Response not successful:', formsResponse.data);
        setIrccForms([]);
      }
      
      // Build URL health map
      if (healthResponse.data?.health) {
        const healthMap = {};
        for (const h of healthResponse.data.health) {
          healthMap[h.formId] = h;
        }
        setUrlHealth(healthMap);
        
        // Show warning if there are broken URLs
        const invalidCount = healthResponse.data.summary?.invalid || 0;
        if (invalidCount > 0) {
          console.warn(`[IRCC] ${invalidCount} form URLs are broken`);
        }
      }
    } catch (error) {
      console.error('Error loading IRCC forms:', error);
      const errorMessage = 'Failed to load IRCC forms: ' + (error.response?.data?.error || error.message);
      showToast(errorMessage, 'error');
      setMessage({ type: 'error', text: errorMessage });
      setIrccForms([]);
    } finally {
      setLoadingIRCC(false);
    }
  }, [axios, showToast]);
  
  // Import a single IRCC form
  const handleImportIRCCForm = async (formId) => {
    setImportingForm(formId);
    setIrccError(null); // Clear any previous error
    try {
      const response = await axios.post('/document-templates/import-ircc', { formId }, {
        timeout: 180000 // 3 minutes timeout for slow IRCC servers
      });
      if (response.data.success) {
        showToast(response.data.message, 'success');
        setMessage({ type: 'success', text: response.data.message });
        loadTemplates();
        loadIRCCForms(); // Refresh to update isImported status
      }
    } catch (error) {
      console.error('Error importing IRCC form:', error);
      const responseData = error.response?.data || {};
      const errorMessage = responseData.error || 
        (error.code === 'ECONNABORTED' ? 'Request timed out. IRCC server may be slow. Please try again.' : 'Failed to import form');
      const isRetryable = responseData.retryable ?? error.code === 'ECONNABORTED';
      const failedUrl = responseData.failedUrl;
      const formCode = responseData.formCode;
      
      // Show error in toast (visible over modal)
      showToast(errorMessage, 'error');
      
      // Also set error state for display in modal
      setIrccError({
        formId,
        formCode,
        message: errorMessage,
        failedUrl,
        retryable: isRetryable
      });
    } finally {
      setImportingForm(null);
    }
  };
  
  // Open IRCC import modal
  const openIRCCImport = () => {
    setShowIRCCModal(true);
    setIrccError(null); // Clear any previous error
    loadIRCCForms();
  };
  
  // Handle file selection
  const handleFileSelect = (file) => {
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      setMessage({ type: 'error', text: 'Only PDF files are allowed' });
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 10MB' });
      return;
    }
    
    // Extract name from filename
    const fileName = file.name.replace('.pdf', '').replace(/[-_]/g, ' ');
    
    setSelectedFile(file);
    setFormData(prev => ({
      ...prev,
      name: fileName,
      formCode: fileName.toUpperCase().replace(/\s+/g, '')
    }));
    setShowModal(true);
  };
  
  // Upload template
  const handleUpload = async () => {
    if (!selectedFile || !formData.name || !formData.formCode || !formData.category) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }
    
    setUploading(true);
    setMessage(null);
    
    try {
      const uploadData = new FormData();
      uploadData.append('pdf', selectedFile);
      uploadData.append('name', formData.name);
      uploadData.append('formCode', formData.formCode);
      uploadData.append('category', formData.category);
      uploadData.append('description', formData.description);
      uploadData.append('version', formData.version);
      
      const response = await axios.post('/document-templates', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: response.data.message || `Template uploaded! Found ${response.data.template.totalFields} fillable fields.`
        });
        setShowModal(false);
        setSelectedFile(null);
        setFormData({
          name: '',
          formCode: '',
          category: 'Work Permit',
          description: '',
          version: '1.0'
        });
        loadTemplates();
      }
    } catch (error) {
      console.error('Error uploading template:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to upload template'
      });
    } finally {
      setUploading(false);
    }
  };
  
  // Delete template
  const handleDelete = async (templateId, templateName) => {
    const confirmed = await confirm({
      title: 'Delete Template',
      message: `Are you sure you want to delete "${templateName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (!confirmed) return;
    
    try {
      const response = await axios.delete(`/document-templates/${templateId}`);
      if (response.data.success) {
        showToast('Template deleted successfully', 'success');
        loadTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      showToast('Failed to delete template', 'error');
    }
  };
  
  // Reprocess all templates (re-apply auto-mapping)
  const [reprocessing, setReprocessing] = useState(false);
  
  const handleReprocessAll = async () => {
    const confirmed = await confirm({
      title: 'Reprocess Templates',
      message: 'This will re-scan all templates and update field mappings. Continue?',
      confirmText: 'Reprocess',
      cancelText: 'Cancel',
      type: 'warning'
    });
    
    if (!confirmed) return;
    
    setReprocessing(true);
    setMessage(null);
    
    try {
      const response = await axios.post('/document-templates/reprocess-all');
      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: response.data.message || `Reprocessed ${response.data.processed} templates. ${response.data.totalMapped} fields auto-mapped.`
        });
        loadTemplates();
      }
    } catch (error) {
      console.error('Error reprocessing templates:', error);
      setMessage({ type: 'error', text: 'Failed to reprocess templates' });
    } finally {
      setReprocessing(false);
    }
  };
  
  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  // Auto-clear messages
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);
  
  return (
    <Section>
      <SectionHeader>
        <SectionTitle>
          <span>📄</span>
          Forms Templates
        </SectionTitle>
        <div style={{ display: 'flex', gap: '8px' }}>
          {showIrcc && (
            <ReprocessButton
              onClick={openIRCCImport}
              title="Import official IRCC forms directly"
              style={{ background: 'rgba(16, 185, 129, 0.2)', borderColor: 'rgba(16, 185, 129, 0.4)' }}
            >
              🍁 Import IRCC Forms
            </ReprocessButton>
          )}
          {templates.length > 0 && (
            <ReprocessButton 
              onClick={handleReprocessAll} 
              disabled={reprocessing}
              $spinning={reprocessing}
              title="Re-scan templates and update auto-mappings"
            >
              {Icons.refresh}
              {reprocessing ? 'Reprocessing...' : 'Re-map Fields'}
            </ReprocessButton>
          )}
        </div>
      </SectionHeader>
      
      <Description>
        Upload blank PDF forms (like IMM1295, IMM5710) to extract fillable fields. 
        These templates can then be auto-filled with client data from the Dashboard.
      </Description>
      
      {message && (
        <StatusMessage $type={message.type}>
          {message.type === 'success' && '✓'}
          {message.type === 'error' && '✕'}
          {message.type === 'info' && 'ℹ'}
          {message.text}
        </StatusMessage>
      )}
      
      {/* Upload Area */}
      <UploadArea
        $isDragOver={isDragOver}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <UploadIcon>📤</UploadIcon>
        <UploadText>
          <strong>Click to upload</strong> or drag and drop
        </UploadText>
        <UploadHint>PDF files only, up to 10MB</UploadHint>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={(e) => handleFileSelect(e.target.files[0])}
          style={{ 
            display: 'none',
            visibility: 'hidden',
            position: 'absolute',
            width: 0,
            height: 0,
            opacity: 0,
            pointerEvents: 'none'
          }}
        />
      </UploadArea>
      
      {/* Templates List */}
      {loading ? (
        <LoadingSpinner />
      ) : templates.length === 0 ? (
        <EmptyState>
          {Icons.document}
          <div>No templates uploaded yet</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            Upload your first PDF template to get started
          </div>
        </EmptyState>
      ) : (
        <TemplateGrid>
          {templates.map(template => (
            <TemplateCard key={template._id} onClick={() => onViewDetails && onViewDetails(template._id)}>
              <TemplateIcon>📄</TemplateIcon>
              <TemplateInfo>
                <TemplateName>
                  {template.name}
                  {template.isShared && (
                    <span
                      title={`Shared from ${template.ownerName || 'contractor'} — read only`}
                      style={{
                        marginLeft: 8, fontSize: 10, fontWeight: 600, verticalAlign: 'middle',
                        color: '#a5b4fc', background: 'rgba(99,102,241,0.15)',
                        border: '1px solid rgba(99,102,241,0.3)', borderRadius: 4, padding: '2px 6px',
                        whiteSpace: 'nowrap'
                      }}
                    >↗ {template.ownerName || 'Shared'}</span>
                  )}
                </TemplateName>
                <TemplateDetails>
                  <CategoryBadge>{template.category}</CategoryBadge>
                  <span>{template.formCode}</span>
                  <FieldCount $mapped={template.mappedFields === template.totalFields}>
                    {template.mappedFields}/{template.totalFields} fields
                  </FieldCount>
                  <QuestionnaireStatus $status={template.questionnaireStatus || 'none'}>
                    {template.questionnaireStatus === 'ready' && `📋 ${template.totalQuestions || 0} Q`}
                    {template.questionnaireStatus === 'generating' && '⏳ Generating...'}
                    {template.questionnaireStatus === 'failed' && '❌ Failed'}
                    {template.questionnaireStatus === 'pending' && '⏳ Pending'}
                    {(!template.questionnaireStatus || template.questionnaireStatus === 'none') && '—'}
                  </QuestionnaireStatus>
                </TemplateDetails>
              </TemplateInfo>
              <TemplateActions>
                <IconButton 
                  title="View details & questionnaire"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails && onViewDetails(template._id);
                  }}
                >
                  {Icons.eye}
                </IconButton>
                {/* Edit/delete are owner-only. A contractor's shared template is
                    read/use-only for a subcontractor. */}
                {!template.isShared && (
                  <>
                    <IconButton title="Edit mappings">
                      {Icons.edit}
                    </IconButton>
                    <DeleteButton
                      title="Delete template"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(template._id, template.name);
                      }}
                    >
                      {Icons.trash}
                    </DeleteButton>
                  </>
                )}
              </TemplateActions>
            </TemplateCard>
          ))}
        </TemplateGrid>
      )}
      
      {/* Upload Modal - rendered via portal */}
      {showModal && createPortal(
        <ModalOverlay onClick={() => !uploading && setShowModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle style={{ margin: 0 }}>Upload Document Template</ModalTitle>
            </ModalHeader>
            
            <ModalBody>
              {selectedFile && (
                <FilePreview>
                  <span style={{ fontSize: 24 }}>📄</span>
                  <div style={{ flex: 1 }}>
                    <FileName>{selectedFile.name}</FileName>
                    <FileSize>{formatFileSize(selectedFile.size)}</FileSize>
                  </div>
                </FilePreview>
              )}
              
              <FormGroup>
                <Label>Template Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Work Permit Application"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Form Code *</Label>
                <Input
                  value={formData.formCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, formCode: e.target.value }))}
                  placeholder="e.g., IMM1295"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Description (optional)</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this form"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Version</Label>
                <Input
                  value={formData.version}
                  onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                  placeholder="e.g., 2024-01"
                />
              </FormGroup>
            </ModalBody>
            
            <ModalActions>
              <Button onClick={() => setShowModal(false)} disabled={uploading}>
                Cancel
              </Button>
              <Button $primary onClick={handleUpload} disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload & Extract Fields'}
              </Button>
            </ModalActions>
          </Modal>
        </ModalOverlay>,
        document.body
      )}
      
      {/* IRCC Import Modal - rendered via portal to ensure proper z-index */}
      {showIrcc && showIRCCModal && createPortal(
        <ModalOverlay onClick={() => !importingForm && setShowIRCCModal(false)}>
          <Modal 
            onClick={e => e.stopPropagation()} 
            style={{ 
              maxWidth: '800px', 
              width: '95vw', 
              maxHeight: '90vh',
              background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
          >
            <ModalHeader style={{ 
              background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.2) 0%, rgba(99, 102, 241, 0.2) 100%)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '20px 24px'
            }}>
              <ModalTitle style={{ 
                margin: 0, 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                fontSize: '20px',
                color: '#ffffff'
              }}>
                <span style={{ fontSize: '28px' }}>🍁</span>
                Import IRCC Forms
              </ModalTitle>
            </ModalHeader>
            
            <ModalBody style={{ 
              maxHeight: '60vh', 
              overflowY: 'auto', 
              padding: '24px',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(99, 102, 241, 0.5) transparent'
            }}>
              <Description style={{ 
                marginBottom: '24px', 
                fontSize: '14px', 
                lineHeight: '1.6',
                color: 'rgba(255, 255, 255, 0.8)',
                background: 'rgba(99, 102, 241, 0.1)',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid rgba(99, 102, 241, 0.2)'
              }}>
                📋 Import official IRCC forms directly from our database. 
                Forms will be automatically processed and ready for client data filling.
              </Description>
              
              {/* Error Banner */}
              {irccError && (
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(185, 28, 28, 0.1) 100%)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '20px' }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: 600, 
                      color: '#f87171', 
                      marginBottom: '6px',
                      fontSize: '14px'
                    }}>
                      Import Failed {irccError.formCode && `- ${irccError.formCode}`}
                    </div>
                    <div style={{ 
                      color: 'rgba(255, 255, 255, 0.8)', 
                      fontSize: '13px',
                      lineHeight: '1.5'
                    }}>
                      {irccError.message}
                    </div>
                    {irccError.failedUrl && (
                      <div style={{ 
                        marginTop: '10px', 
                        fontSize: '11px', 
                        color: 'rgba(255, 255, 255, 0.5)',
                        wordBreak: 'break-all',
                        background: 'rgba(0, 0, 0, 0.2)',
                        padding: '8px',
                        borderRadius: '4px',
                        fontFamily: 'monospace'
                      }}>
                        🔗 Failed URL: {irccError.failedUrl}
                      </div>
                    )}
                    {irccError.retryable ? (
                      <div style={{ 
                        marginTop: '10px', 
                        fontSize: '12px', 
                        color: 'rgba(255, 255, 255, 0.6)' 
                      }}>
                        💡 Tip: IRCC servers can be slow. Try again in a few moments.
                      </div>
                    ) : (
                      <div style={{ 
                        marginTop: '10px', 
                        fontSize: '12px', 
                        color: '#fbbf24' 
                      }}>
                        🔧 The form URL may have changed. Please contact admin to update it.
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setIrccError(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.5)',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '4px'
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}
              
              {loadingIRCC ? (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <LoadingSpinner />
                  <p style={{ marginTop: '16px', color: 'rgba(255,255,255,0.6)', fontSize: '15px' }}>
                    Loading available forms...
                  </p>
                </div>
              ) : irccForms.length === 0 ? (
                <EmptyState style={{ padding: '60px 20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                  <div style={{ fontSize: '16px', fontWeight: 600 }}>No IRCC forms available</div>
                  <div style={{ fontSize: '13px', marginTop: '8px', color: 'rgba(255,255,255,0.5)' }}>
                    Go to Profile → IRCC Forms Manager to add forms
                  </div>
                </EmptyState>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Broken URLs Warning Banner */}
                  {Object.values(urlHealth).filter(h => h.status === 'invalid').length > 0 && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(185, 28, 28, 0.1) 100%)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontSize: '18px' }}>⚠️</span>
                      <div>
                        <div style={{ fontWeight: 600, color: '#f87171', fontSize: '13px' }}>
                          {Object.values(urlHealth).filter(h => h.status === 'invalid').length} form URL(s) are broken
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '2px' }}>
                          IRCC may have updated these forms. Contact admin to update URLs.
                        </div>
                      </div>
                    </div>
                  )}
                  {irccForms.map((form, index) => (
                    <div
                      key={form.formId}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto auto',
                        alignItems: 'center',
                        padding: '18px 20px',
                        background: form.isImported 
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)' 
                          : 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)',
                        border: form.isImported 
                          ? '1px solid rgba(16, 185, 129, 0.4)'
                          : '1px solid rgba(99, 102, 241, 0.3)',
                        borderRadius: '12px',
                        gap: '16px',
                        transition: 'all 0.2s ease',
                        cursor: 'default'
                      }}
                      onMouseEnter={(e) => {
                        if (!form.isImported) {
                          e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.6)';
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = form.isImported 
                          ? 'rgba(16, 185, 129, 0.4)' 
                          : 'rgba(99, 102, 241, 0.3)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                        <span style={{ 
                          fontSize: '32px', 
                          lineHeight: 1,
                          filter: form.isImported ? 'grayscale(0.3)' : 'none'
                        }}>📄</span>
                        <div>
                          <div style={{ 
                            fontWeight: 700, 
                            color: '#ffffff',
                            fontSize: '16px',
                            marginBottom: '6px',
                            fontFamily: "'Monaco', 'Consolas', monospace"
                          }}>
                            {form.formCode}
                          </div>
                          <div style={{ 
                            fontSize: '14px', 
                            color: 'rgba(255,255,255,0.75)',
                            lineHeight: '1.5'
                          }}>
                            {form.formName}
                          </div>
                          {form.hasBarcodes && (
                            <div style={{ 
                              fontSize: '11px', 
                              color: '#fbbf24', 
                              marginTop: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              background: 'rgba(251, 191, 36, 0.1)',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              width: 'fit-content',
                              lineHeight: '1.4'
                            }}>
                              ⚠️ Dynamic PDF - Download and open with Adobe Acrobat Reader
                            </div>
                          )}
                          {/* Broken URL Warning */}
                          {urlHealth[form.formId]?.status === 'invalid' && (
                            <div style={{ 
                              fontSize: '11px', 
                              color: '#ef4444', 
                              marginTop: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              background: 'rgba(239, 68, 68, 0.1)',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              width: 'fit-content',
                              lineHeight: '1.4'
                            }}>
                              🔗 URL Broken - Form cannot be imported until URL is updated
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Download button */}
                      {form.downloadUrl && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (form.hasBarcodes) {
                              showToast('This is a dynamic PDF form. Download and open with Adobe Acrobat Reader.', 'warning', {
                                title: 'Dynamic PDF',
                                duration: 6000
                              });
                            }
                            window.open(form.downloadUrl, '_blank');
                          }}
                          style={{ 
                            padding: '10px 16px',
                            fontSize: '13px',
                            background: 'rgba(59, 130, 246, 0.2)',
                            border: '1px solid rgba(59, 130, 246, 0.4)',
                            minWidth: 'auto'
                          }}
                          title={form.hasBarcodes ? "Download PDF (requires Adobe Acrobat Reader)" : "Download from IRCC"}
                        >
                          ⬇️ PDF
                        </Button>
                      )}
                      
                      {form.isImported ? (
                        <div style={{ 
                          color: '#10b981', 
                          fontSize: '14px',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: 'rgba(16, 185, 129, 0.15)',
                          padding: '10px 16px',
                          borderRadius: '8px'
                        }}>
                          ✓ Imported
                        </div>
                      ) : (
                        <Button
                          $primary
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImportIRCCForm(form.formId);
                          }}
                          disabled={importingForm !== null}
                          style={{ 
                            padding: '10px 24px',
                            fontSize: '14px',
                            fontWeight: 600,
                            minWidth: '100px'
                          }}
                        >
                          {importingForm === form.formId ? '⏳ Importing...' : '📥 Import'}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ModalBody>
            
            <ModalActions style={{ 
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '20px 24px',
              background: 'rgba(15, 23, 42, 0.5)'
            }}>
              <div style={{ 
                flex: 1, 
                fontSize: '14px', 
                color: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ 
                  background: 'rgba(16, 185, 129, 0.2)', 
                  color: '#34d399',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontWeight: 600
                }}>
                  {irccForms.filter(f => f.isImported).length}
                </span>
                <span>of</span>
                <span style={{ 
                  background: 'rgba(99, 102, 241, 0.2)', 
                  color: '#a5b4fc',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontWeight: 600
                }}>
                  {irccForms.length}
                </span>
                <span>forms imported</span>
              </div>
              <Button 
                onClick={() => setShowIRCCModal(false)} 
                disabled={importingForm !== null} 
                style={{ padding: '12px 28px', fontSize: '14px' }}
              >
                Close
              </Button>
            </ModalActions>
          </Modal>
        </ModalOverlay>,
        document.body
      )}
    </Section>
  );
};

export default DocumentTemplates;

